/**
 * EmailHub — central communications hub for The Archive.
 *
 * Single admin module at /admin/settings/email. Sub-tabs:
 *   1. Overview       — dashboard metrics + Resend health banner
 *   2. Compose        — individual email composer with autocomplete + templates
 *   3. Bulk Campaign  — audience selector + bulk send
 *   4. Newsletters    — legacy newsletter queue/preview/send (reused)
 *   5. Templates      — CRUD for reusable email templates
 *   6. Drafts         — saved drafts
 *   7. History        — recent sends with filters + retry
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Mail, Send, Users, FileText, Edit3, History, Activity,
  RefreshCw, Search, Plus, Trash2, Save, X, Check, AlertCircle, TestTube2,
  Loader2,
} from "lucide-react";
import {
  adminEmailHubDashboard, adminEmailHubRecentSends,
  adminEmailHubTemplatesList, adminEmailHubTemplateCreate,
  adminEmailHubTemplateUpdate, adminEmailHubTemplateDelete,
  adminEmailHubDraftsList, adminEmailHubDraftCreate,
  adminEmailHubDraftUpdate, adminEmailHubDraftDelete,
  adminEmailHubAudiencePreview, adminEmailHubSendBulk,
  adminEmailHubSendTest, adminEmailHubMemberSearch,
  adminEmailHealth, adminMemberSendEmail,
} from "../../lib/api";
import { EmailDeliveryBanner } from "./EmailHealthPanel";
import { emitArchiveInvalidate, useArchiveInvalidate } from "../../lib/archive-events";

const TABS = [
  { key: "overview",   label: "Overview",   icon: Activity },
  { key: "compose",    label: "Compose",    icon: Send },
  { key: "bulk",       label: "Bulk",       icon: Users },
  { key: "templates",  label: "Templates",  icon: FileText },
  { key: "drafts",     label: "Drafts",     icon: Edit3 },
  { key: "history",    label: "History",    icon: History },
];

const STATUS_COLOR = {
  delivered: "#5DD4B3",
  "delivered.confirmed": "#5DD4B3",
  opened: "#9FA8D4",
  clicked: "#9FA8D4",
  failed: "#D92D20",
  bounced: "#D92D20",
  suppressed: "#D92D20",
  queued: "#CBB892",
  queued_disabled: "#E0A526",
  retry_pending: "#CBB892",
  sending: "#CBB892",
};

export const EmailHub = ({ token, userToken }) => {
  const [tab, setTab] = useState("overview");

  return (
    <div data-testid="email-hub-shell">
      <div className="flex items-end justify-between gap-3 mb-3">
        <div>
          <div className="font-mono text-[10px] tracking-[0.3em] text-[#D92D20] mb-2">// COMMUNICATIONS</div>
          <h1 className="font-display text-4xl text-[#E4DFD3]">Email Hub</h1>
          <p className="text-[#A39E93] mt-1 text-sm max-w-2xl">
            Central command for every Archive email — individual messages, bulk
            campaigns, newsletter releases, templates and drafts. All sends go
            through Resend and are audit-logged.
          </p>
        </div>
      </div>

      <div className="mb-4">
        <EmailDeliveryBanner token={token} userToken={userToken} />
      </div>

      <div className="flex flex-wrap gap-1 my-4 border-b border-[#2D2A26]" data-testid="email-hub-tabs">
        {TABS.map((t) => {
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`email-hub-tab-${t.key}`}
              className={"flex items-center gap-2 px-4 py-2 -mb-px font-mono text-[10px] uppercase tracking-[0.25em] border-b-2 " +
                (active ? "border-[#D92D20] text-[#D92D20]" : "border-transparent text-[#A39E93] hover:text-[#E4DFD3]")}
            >
              <t.icon size={12} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "overview"  && <OverviewTab  token={token} userToken={userToken} />}
      {tab === "compose"   && <ComposeTab   token={token} userToken={userToken} />}
      {tab === "bulk"      && <BulkTab      token={token} userToken={userToken} />}
      {tab === "templates" && <TemplatesTab token={token} userToken={userToken} />}
      {tab === "drafts"    && <DraftsTab    token={token} userToken={userToken} />}
      {tab === "history"   && <HistoryTab   token={token} userToken={userToken} />}
    </div>
  );
};

// ---------- Overview ----------------------------------------------------

const Stat = ({ label, value, color = "#E4DFD3", testid }) => (
  <div className="border border-[#2D2A26] bg-[#131313] p-4" data-testid={testid}>
    <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065] mb-1">{label}</div>
    <div className="font-display text-3xl" style={{ color }}>{value ?? "—"}</div>
  </div>
);

const OverviewTab = ({ token, userToken }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminEmailHubDashboard(token, userToken);
      setData(r);
    } catch { toast.error("Failed to load dashboard"); }
    finally { setLoading(false); }
  }, [token, userToken]);
  useEffect(() => { load(); }, [load]);
  useArchiveInvalidate("emails", load);

  if (loading && !data) return <p className="font-mono text-xs text-[#757065]">Loading…</p>;
  if (!data) return null;

  return (
    <div data-testid="email-hub-overview">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-6">
        <Stat label="Sent Today"        value={data.sent_today}        color="#5DD4B3" testid="stat-sent-today" />
        <Stat label="Sent 7d"           value={data.sent_7d}           color="#5DD4B3" testid="stat-sent-7d" />
        <Stat label="Sent 30d"          value={data.sent_30d}          color="#5DD4B3" testid="stat-sent-30d" />
        <Stat label="Failed 30d"        value={data.failed_30d}        color="#D92D20" testid="stat-failed-30d" />
        <Stat label="In Queue"          value={data.queued}            color="#CBB892" testid="stat-queued" />
        <Stat label="Drafts"            value={data.drafts}            color="#9FA8D4" testid="stat-drafts" />
        <Stat label="Templates"         value={data.templates}         color="#9FA8D4" testid="stat-templates" />
        <Stat label="Newsletter Camp."  value={data.newsletter_campaigns} color="#9FA8D4" testid="stat-newsletters" />
        <Stat label="Audience Opt-In"   value={data.audience_opted_in} color="#E0A526" testid="stat-audience" />
        <Stat label="Members Total"     value={data.members_total}     color="#E4DFD3" testid="stat-members" />
      </div>

      <div className="border border-[#2D2A26] bg-[#0a0a0a] p-4 mb-6" data-testid="email-hub-chart">
        <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065] mb-3">// 30-day delivery</div>
        <div className="flex items-end gap-1 h-32">
          {(data.chart_30d || []).map((d, i) => {
            const max = Math.max(1, ...(data.chart_30d || []).map((x) => x.sent + x.failed));
            const hSent = (d.sent / max) * 100;
            const hFailed = (d.failed / max) * 100;
            return (
              <div key={d.date} className="flex-1 flex flex-col-reverse items-center" title={`${d.date}: ${d.sent} sent, ${d.failed} failed`}>
                <div className="w-full bg-[#5DD4B3]" style={{ height: `${hSent}%` }} />
                <div className="w-full bg-[#D92D20]" style={{ height: `${hFailed}%` }} />
              </div>
            );
          })}
        </div>
      </div>

      <button onClick={load} className="flex items-center gap-2 border border-[#454038] hover:border-[#D92D20] text-[#A39E93] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em]" data-testid="email-hub-refresh-overview">
        <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
      </button>
    </div>
  );
};

// ---------- Compose (individual) ---------------------------------------

const MemberAutocomplete = ({ token, userToken, value, onChange, onPick }) => {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounce = useRef(null);

  useEffect(() => {
    if (!value || value.length < 2) { setRows([]); return; }
    setLoading(true);
    clearTimeout(debounce.current);
    debounce.current = setTimeout(async () => {
      try {
        const r = await adminEmailHubMemberSearch(token, value, userToken);
        setRows(r.rows || []); setOpen(true);
      } catch { setRows([]); }
      finally { setLoading(false); }
    }, 220);
    return () => clearTimeout(debounce.current);
  }, [value, token, userToken]);

  return (
    <div className="relative">
      <div className="flex items-center gap-2 border border-[#2D2A26] bg-[#0F0F0F] px-3 py-2">
        <Search size={12} className="text-[#757065]" />
        <input
          type="text" placeholder="Search by callsign, email, or designation…"
          value={value} onChange={(e) => onChange(e.target.value)}
          onFocus={() => rows.length > 0 && setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 200)}
          data-testid="compose-autocomplete-input"
          className="flex-1 bg-transparent outline-none text-sm text-[#E4DFD3] placeholder:text-[#757065]"
        />
        {loading && <Loader2 size={12} className="animate-spin text-[#757065]" />}
      </div>
      {open && rows.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 max-h-64 overflow-y-auto border border-[#2D2A26] bg-[#0F0F0F] z-30" data-testid="compose-autocomplete-list">
          {rows.map((m) => (
            <button
              key={m.id}
              onClick={() => { onPick(m); setOpen(false); }}
              className="w-full text-left px-3 py-2 hover:bg-[#1a0808] border-b border-[#2D2A26]/50 last:border-b-0"
              data-testid={`compose-autocomplete-item-${m.id}`}
            >
              <div className="flex items-center gap-2">
                <span className="font-display text-sm text-[#E4DFD3]">{m.callsign || "—"}</span>
                <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#757065]">{m.tier}</span>
                {m.suppressed && <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#D92D20]">SUPPRESSED</span>}
              </div>
              <div className="font-mono text-[10px] text-[#A39E93] truncate">{m.email}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const ComposeTab = ({ token, userToken }) => {
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [templates, setTemplates] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);

  useEffect(() => {
    adminEmailHubTemplatesList(token, userToken)
      .then((r) => setTemplates((r.rows || []).filter((t) => t.kind === "individual" || t.kind === "campaign")))
      .catch(() => {});
  }, [token, userToken]);

  const send = async () => {
    if (!picked || !picked.id) { toast.error("Pick a recipient first"); return; }
    if (!subject.trim() || !body.trim()) { toast.error("Subject and body are required"); return; }
    setBusy(true);
    try {
      const r = await adminMemberSendEmail(token, picked.id, subject, body, userToken);
      if (r.deduped) toast.success("Already sent recently (idempotent dedup)");
      else if (r.message?.status === "delivered") toast.success(`Delivered to ${picked.email}`);
      else if (r.message?.status === "queued_disabled") toast("Queued (delivery currently disabled)");
      else toast.success(`Status: ${r.message?.status}`);
      setSubject(""); setBody(""); setPicked(null); setQuery("");
      emitArchiveInvalidate(["emails", "members"]);
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    finally { setBusy(false); }
  };

  const saveDraft = async () => {
    if (!subject.trim() && !body.trim()) { toast.error("Nothing to save"); return; }
    try {
      await adminEmailHubDraftCreate(token, {
        name: subject || "Untitled draft", subject, body,
        kind: "individual", recipient_member_id: picked?.id || null,
      }, userToken);
      toast.success("Draft saved");
      emitArchiveInvalidate("emails");
    } catch { toast.error("Failed to save draft"); }
  };

  const applyTemplate = (t) => {
    setSubject(t.subject); setBody(t.body);
    toast.success(`Template "${t.name}" applied`);
  };

  return (
    <div className="grid lg:grid-cols-3 gap-4" data-testid="email-hub-compose">
      <div className="lg:col-span-2 border border-[#2D2A26] bg-[#0a0a0a] p-5 space-y-4">
        <div>
          <label className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065] block mb-2">RECIPIENT</label>
          {picked ? (
            <div className="flex items-center justify-between border border-[#5DD4B3]/40 bg-[#5DD4B3]/10 px-3 py-2">
              <div>
                <div className="font-display text-base text-[#E4DFD3]">{picked.callsign}</div>
                <div className="font-mono text-xs text-[#A39E93]">{picked.email}</div>
              </div>
              <button onClick={() => { setPicked(null); setQuery(""); }} className="text-[#A39E93] hover:text-[#D92D20]" data-testid="compose-recipient-clear"><X size={14} /></button>
            </div>
          ) : (
            <MemberAutocomplete token={token} userToken={userToken} value={query} onChange={setQuery} onPick={setPicked} />
          )}
        </div>
        <div>
          <label className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065] block mb-2">SUBJECT</label>
          <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Editorial brief — week of…" data-testid="compose-subject"
            className="w-full bg-[#0F0F0F] border border-[#2D2A26] focus:border-[#D92D20] outline-none px-3 py-2 font-mono text-sm text-[#E4DFD3] placeholder:text-[#757065]" />
        </div>
        <div>
          <label className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065] block mb-2">BODY</label>
          <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={12} placeholder="Plain text. Will be wrapped in The Archive's HTML shell." data-testid="compose-body"
            className="w-full bg-[#0F0F0F] border border-[#2D2A26] focus:border-[#D92D20] outline-none px-3 py-2 font-mono text-sm text-[#E4DFD3] placeholder:text-[#757065] resize-y" />
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={send} disabled={busy || !picked} data-testid="compose-send"
            className="flex items-center gap-2 bg-[#D92D20] hover:bg-[#B92318] disabled:opacity-40 text-white px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em]">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Send
          </button>
          <button onClick={saveDraft} data-testid="compose-save-draft"
            className="flex items-center gap-2 border border-[#454038] hover:border-[#E0A526] text-[#E0A526] px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em]">
            <Save size={12} /> Save Draft
          </button>
          <button onClick={() => setShowTestModal(true)} data-testid="compose-test"
            className="flex items-center gap-2 border border-[#454038] hover:border-[#9FA8D4] text-[#9FA8D4] px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em]">
            <TestTube2 size={12} /> Send Test
          </button>
        </div>
      </div>

      <div className="border border-[#2D2A26] bg-[#0a0a0a] p-5">
        <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065] mb-3">// TEMPLATES</div>
        {templates.length === 0 ? (
          <p className="font-mono text-xs text-[#757065]">No templates yet. Build one in the Templates tab.</p>
        ) : (
          <div className="space-y-2 max-h-[70vh] overflow-y-auto">
            {templates.map((t) => (
              <button key={t.id} onClick={() => applyTemplate(t)} className="block w-full text-left border border-[#2D2A26] hover:border-[#D92D20] px-3 py-2"
                data-testid={`compose-template-${t.id}`}>
                <div className="font-display text-sm text-[#E4DFD3]">{t.name}</div>
                <div className="font-mono text-[10px] text-[#A39E93] truncate">{t.subject}</div>
              </button>
            ))}
          </div>
        )}
      </div>

      {showTestModal && (
        <TestEmailModal token={token} userToken={userToken} subject={subject} body={body} onClose={() => setShowTestModal(false)} />
      )}
    </div>
  );
};

const TestEmailModal = ({ token, userToken, subject, body, onClose }) => {
  const [recipient, setRecipient] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!subject || !body) { toast.error("Compose subject + body first"); return; }
    setBusy(true);
    try {
      const r = await adminEmailHubSendTest(token, { subject, body, recipient: recipient || undefined }, userToken);
      toast.success(`Test ${r.ok ? "sent" : "queued"}: ${r.status}`);
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Test failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="test-email-modal">
      <div className="w-full max-w-md border border-[#D92D20]/40 bg-[#0F0F0F] p-5">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20] mb-3">// SEND TEST EMAIL</div>
        <p className="font-mono text-xs text-[#A39E93] mb-3">Sends the current subject + body to your own inbox (or override below). No member row is created.</p>
        <input type="email" value={recipient} onChange={(e) => setRecipient(e.target.value)} placeholder="leave blank for admin inbox" data-testid="test-email-recipient"
          className="w-full bg-[#0a0a0a] border border-[#2D2A26] focus:border-[#D92D20] outline-none px-3 py-2 font-mono text-sm text-[#E4DFD3] placeholder:text-[#757065] mb-3" />
        <div className="flex gap-2">
          <button onClick={send} disabled={busy} data-testid="test-email-send" className="flex-1 flex items-center justify-center gap-2 bg-[#D92D20] hover:bg-[#B92318] disabled:opacity-40 text-white px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em]">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Send Test
          </button>
          <button onClick={onClose} className="px-3 py-2 border border-[#454038] text-[#A39E93] font-mono text-[10px] uppercase tracking-[0.25em]">Cancel</button>
        </div>
      </div>
    </div>
  );
};

// ---------- Bulk Campaign ---------------------------------------------

const BulkTab = ({ token, userToken }) => {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [filter, setFilter] = useState({
    all_members: false, tiers: [], clearance_levels: [], founding_only: false,
    tags: [], newsletter_status: "opted_in", activity: null,
  });
  const [tagText, setTagText] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);

  const previewAudience = useCallback(async () => {
    setPreviewLoading(true);
    try {
      const r = await adminEmailHubAudiencePreview(token, filter, userToken);
      setPreview(r);
    } catch { toast.error("Failed to preview audience"); }
    finally { setPreviewLoading(false); }
  }, [token, userToken, filter]);

  useEffect(() => { previewAudience(); }, [previewAudience]);

  const toggleArr = (key, val) => {
    setFilter((f) => ({
      ...f,
      [key]: (f[key] || []).includes(val) ? f[key].filter((x) => x !== val) : [...(f[key] || []), val],
    }));
  };

  const submitTag = () => {
    const t = tagText.trim().toLowerCase();
    if (!t) return;
    if (!(filter.tags || []).includes(t)) {
      setFilter((f) => ({ ...f, tags: [...(f.tags || []), t] }));
    }
    setTagText("");
  };

  const send = async (testMode) => {
    if (!subject.trim() || !body.trim()) { toast.error("Subject and body required"); return; }
    if (!preview || preview.count === 0) { toast.error("Audience is empty"); return; }
    if (!testMode && !window.confirm(`Send to ${preview.count} member${preview.count === 1 ? "" : "s"}? This cannot be undone.`)) return;
    setBusy(true);
    try {
      const r = await adminEmailHubSendBulk(token, { subject, body, audience: filter, test_mode: !!testMode }, userToken);
      toast.success(`Sent: ${r.sent} · Failed: ${r.failed} · Disabled: ${r.disabled} · Deduped: ${r.skipped_dedup}`);
      emitArchiveInvalidate(["emails", "members"]);
      if (!testMode) { setSubject(""); setBody(""); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk send failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-4" data-testid="email-hub-bulk">
      <div className="lg:col-span-2 border border-[#2D2A26] bg-[#0a0a0a] p-5 space-y-4">
        <div>
          <label className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065] block mb-2">SUBJECT</label>
          <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="The Archive — Major Release" data-testid="bulk-subject"
            className="w-full bg-[#0F0F0F] border border-[#2D2A26] focus:border-[#D92D20] outline-none px-3 py-2 font-mono text-sm text-[#E4DFD3] placeholder:text-[#757065]" />
        </div>
        <div>
          <label className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065] block mb-2">BODY</label>
          <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={14} data-testid="bulk-body"
            className="w-full bg-[#0F0F0F] border border-[#2D2A26] focus:border-[#D92D20] outline-none px-3 py-2 font-mono text-sm text-[#E4DFD3] placeholder:text-[#757065] resize-y" />
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => send(false)} disabled={busy || !preview?.count} data-testid="bulk-send"
            className="flex items-center gap-2 bg-[#D92D20] hover:bg-[#B92318] disabled:opacity-40 text-white px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em]">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Send to {preview?.count || 0} Members
          </button>
          <button onClick={() => send(true)} disabled={busy} data-testid="bulk-send-test"
            className="flex items-center gap-2 border border-[#454038] hover:border-[#9FA8D4] text-[#9FA8D4] px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em]">
            <TestTube2 size={12} /> Send To First 3 (Test)
          </button>
        </div>
      </div>

      <div className="border border-[#2D2A26] bg-[#0a0a0a] p-5 space-y-3" data-testid="bulk-audience-panel">
        <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#D92D20]">// AUDIENCE</div>

        <div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={!!filter.all_members} onChange={(e) => setFilter((f) => ({ ...f, all_members: e.target.checked }))} data-testid="bulk-all-members" />
            <span className="font-mono text-[11px] text-[#E4DFD3] uppercase tracking-[0.2em]">All Members</span>
          </label>
        </div>

        <div>
          <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065] mb-1">Tier</div>
          <div className="flex flex-wrap gap-1">
            {["subscriber", "member"].map((t) => (
              <button key={t} onClick={() => toggleArr("tiers", t)} data-testid={`bulk-tier-${t}`}
                className={"px-2 py-1 border font-mono text-[10px] uppercase tracking-[0.2em] " +
                  ((filter.tiers || []).includes(t) ? "border-[#D92D20] text-[#D92D20] bg-[#1a0808]" : "border-[#2D2A26] text-[#A39E93]")}>{t}</button>
            ))}
          </div>
        </div>

        <div>
          <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065] mb-1">Clearance Level</div>
          <div className="flex flex-wrap gap-1">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} onClick={() => toggleArr("clearance_levels", n)} data-testid={`bulk-clearance-${n}`}
                className={"px-2 py-1 border font-mono text-[10px] " +
                  ((filter.clearance_levels || []).includes(n) ? "border-[#E0A526] text-[#E0A526] bg-[#1a0808]" : "border-[#2D2A26] text-[#A39E93]")}>{n}★</button>
            ))}
          </div>
        </div>

        <div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={!!filter.founding_only} onChange={(e) => setFilter((f) => ({ ...f, founding_only: e.target.checked }))} data-testid="bulk-founding" />
            <span className="font-mono text-[11px] text-[#E0A526] uppercase tracking-[0.2em]">Founding Members Only</span>
          </label>
        </div>

        <div>
          <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065] mb-1">Newsletter</div>
          <div className="flex flex-wrap gap-1">
            {[
              { v: "opted_in", l: "Opted In" },
              { v: "opted_out", l: "Opted Out" },
              { v: "either", l: "Either" },
            ].map((o) => (
              <button key={o.v} onClick={() => setFilter((f) => ({ ...f, newsletter_status: o.v }))} data-testid={`bulk-newsletter-${o.v}`}
                className={"px-2 py-1 border font-mono text-[10px] uppercase tracking-[0.2em] " +
                  (filter.newsletter_status === o.v ? "border-[#D92D20] text-[#D92D20] bg-[#1a0808]" : "border-[#2D2A26] text-[#A39E93]")}>{o.l}</button>
            ))}
          </div>
        </div>

        <div>
          <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065] mb-1">Activity</div>
          <div className="flex flex-wrap gap-1">
            {[
              { v: null, l: "Any" },
              { v: "last_30d", l: "Last 30d" },
              { v: "last_7d", l: "Last 7d" },
            ].map((o) => (
              <button key={String(o.v)} onClick={() => setFilter((f) => ({ ...f, activity: o.v }))} data-testid={`bulk-activity-${o.v ?? 'any'}`}
                className={"px-2 py-1 border font-mono text-[10px] uppercase tracking-[0.2em] " +
                  ((filter.activity ?? null) === o.v ? "border-[#5DD4B3] text-[#5DD4B3] bg-[#0a1a13]" : "border-[#2D2A26] text-[#A39E93]")}>{o.l}</button>
            ))}
          </div>
        </div>

        <div>
          <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065] mb-1">Tags</div>
          <div className="flex gap-1">
            <input value={tagText} onChange={(e) => setTagText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), submitTag())}
              placeholder="tag" data-testid="bulk-tag-input"
              className="flex-1 bg-[#0F0F0F] border border-[#2D2A26] focus:border-[#D92D20] outline-none px-2 py-1 font-mono text-xs text-[#E4DFD3]" />
            <button onClick={submitTag} className="border border-[#454038] text-[#A39E93] px-2 font-mono text-[10px] uppercase">Add</button>
          </div>
          <div className="flex flex-wrap gap-1 mt-2">
            {(filter.tags || []).map((t) => (
              <span key={t} className="inline-flex items-center gap-1 px-2 py-0.5 border border-[#9FA8D4]/40 text-[#9FA8D4] font-mono text-[10px]" data-testid={`bulk-tag-${t}`}>
                {t}
                <button onClick={() => setFilter((f) => ({ ...f, tags: (f.tags || []).filter((x) => x !== t) }))} className="hover:text-[#D92D20]"><X size={10} /></button>
              </span>
            ))}
          </div>
        </div>

        <div className="border-t border-[#2D2A26] pt-3">
          <div className="flex items-center justify-between">
            <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065]">// AUDIENCE PREVIEW</div>
            <button onClick={previewAudience} data-testid="bulk-refresh-preview"
              className="text-[#A39E93] hover:text-[#D92D20]"><RefreshCw size={12} className={previewLoading ? "animate-spin" : ""} /></button>
          </div>
          <div className="font-display text-3xl text-[#E0A526] mt-1" data-testid="bulk-audience-count">{preview?.count ?? "—"}</div>
          {preview?.sample && preview.sample.length > 0 && (
            <div className="mt-2 space-y-1 max-h-32 overflow-y-auto">
              {preview.sample.slice(0, 6).map((m, i) => (
                <div key={m.id || i} className="font-mono text-[10px] text-[#A39E93] truncate">
                  · {m.callsign} <span className="text-[#757065]">({m.email})</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ---------- Templates -------------------------------------------------

const TemplatesTab = ({ token, userToken }) => {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminEmailHubTemplatesList(token, userToken);
      setRows(r.rows || []);
    } catch { toast.error("Failed to load templates"); }
    finally { setLoading(false); }
  }, [token, userToken]);
  useEffect(() => { load(); }, [load]);

  const startNew = () => setEditing({ name: "", subject: "", body: "", kind: "individual" });
  const startEdit = (t) => setEditing({ ...t });

  const save = async () => {
    const payload = {
      name: editing.name?.trim() || "Untitled",
      subject: editing.subject || "",
      body: editing.body || "",
      kind: editing.kind || "individual",
    };
    if (!payload.subject || !payload.body) { toast.error("Subject and body required"); return; }
    try {
      if (editing.id) await adminEmailHubTemplateUpdate(token, editing.id, payload, userToken);
      else await adminEmailHubTemplateCreate(token, payload, userToken);
      toast.success("Template saved");
      setEditing(null); load();
      emitArchiveInvalidate("emails");
    } catch { toast.error("Failed to save"); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try {
      await adminEmailHubTemplateDelete(token, t.id, userToken);
      toast.success("Template deleted");
      load();
      emitArchiveInvalidate("emails");
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="email-hub-templates">
      <div className="flex items-center justify-between mb-3">
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39E93]">{rows.length} template{rows.length === 1 ? "" : "s"}</span>
        <button onClick={startNew} data-testid="template-new"
          className="flex items-center gap-2 bg-[#D92D20] hover:bg-[#B92318] text-white px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em]">
          <Plus size={12} /> New Template
        </button>
      </div>
      {loading && rows.length === 0 ? <p className="font-mono text-xs text-[#757065]">Loading…</p> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {rows.map((t) => (
            <div key={t.id} className="border border-[#2D2A26] bg-[#0a0a0a] p-4" data-testid={`template-row-${t.id}`}>
              <div className="font-display text-lg text-[#E4DFD3] mb-1">{t.name}</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#A39E93] mb-2">{t.kind} · {t.subject}</div>
              <p className="text-xs text-[#A39E93] line-clamp-3 mb-3">{t.body}</p>
              <div className="flex gap-2">
                <button onClick={() => startEdit(t)} className="flex-1 border border-[#454038] hover:border-[#9FA8D4] text-[#9FA8D4] px-2 py-1.5 font-mono text-[10px] uppercase">Edit</button>
                <button onClick={() => remove(t)} className="border border-[#D92D20]/40 text-[#D92D20] hover:bg-[#D92D20]/15 px-2 py-1.5 font-mono text-[10px] uppercase">Delete</button>
              </div>
            </div>
          ))}
          {rows.length === 0 && !loading && (
            <div className="col-span-full border border-dashed border-[#2D2A26] p-8 text-center font-mono text-xs text-[#757065]">
              No templates yet. Build one to reuse across compose and bulk.
            </div>
          )}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="template-editor">
          <div className="w-full max-w-2xl border border-[#D92D20]/40 bg-[#0F0F0F] p-5 space-y-3">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20]">// {editing.id ? "EDIT" : "NEW"} TEMPLATE</div>
            <input value={editing.name} onChange={(e) => setEditing((s) => ({ ...s, name: e.target.value }))} placeholder="Template name" data-testid="template-name"
              className="w-full bg-[#0a0a0a] border border-[#2D2A26] focus:border-[#D92D20] outline-none px-3 py-2 font-mono text-sm text-[#E4DFD3]" />
            <select value={editing.kind} onChange={(e) => setEditing((s) => ({ ...s, kind: e.target.value }))} data-testid="template-kind"
              className="w-full bg-[#0a0a0a] border border-[#2D2A26] focus:border-[#D92D20] outline-none px-3 py-2 font-mono text-sm text-[#E4DFD3]">
              <option value="individual">Individual</option>
              <option value="newsletter">Newsletter</option>
              <option value="campaign">Campaign</option>
            </select>
            <input value={editing.subject} onChange={(e) => setEditing((s) => ({ ...s, subject: e.target.value }))} placeholder="Subject" data-testid="template-subject"
              className="w-full bg-[#0a0a0a] border border-[#2D2A26] focus:border-[#D92D20] outline-none px-3 py-2 font-mono text-sm text-[#E4DFD3]" />
            <textarea value={editing.body} onChange={(e) => setEditing((s) => ({ ...s, body: e.target.value }))} rows={10} placeholder="Body" data-testid="template-body"
              className="w-full bg-[#0a0a0a] border border-[#2D2A26] focus:border-[#D92D20] outline-none px-3 py-2 font-mono text-sm text-[#E4DFD3] resize-y" />
            <div className="flex gap-2">
              <button onClick={save} data-testid="template-save" className="flex-1 bg-[#D92D20] hover:bg-[#B92318] text-white px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em]">Save</button>
              <button onClick={() => setEditing(null)} className="px-3 py-2 border border-[#454038] text-[#A39E93] font-mono text-[10px] uppercase tracking-[0.25em]">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------- Drafts ----------------------------------------------------

const DraftsTab = ({ token, userToken }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminEmailHubDraftsList(token, userToken);
      setRows(r.rows || []);
    } catch { toast.error("Failed to load drafts"); }
    finally { setLoading(false); }
  }, [token, userToken]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      if (editing.id) await adminEmailHubDraftUpdate(token, editing.id, editing, userToken);
      else await adminEmailHubDraftCreate(token, editing, userToken);
      toast.success("Draft saved");
      setEditing(null); load();
    } catch { toast.error("Save failed"); }
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete draft "${d.name}"?`)) return;
    try {
      await adminEmailHubDraftDelete(token, d.id, userToken);
      toast.success("Deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="email-hub-drafts">
      <div className="flex items-center justify-between mb-3">
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39E93]">{rows.length} draft{rows.length === 1 ? "" : "s"}</span>
        <button onClick={() => setEditing({ name: "", subject: "", body: "", kind: "individual" })}
          className="flex items-center gap-2 bg-[#D92D20] hover:bg-[#B92318] text-white px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em]" data-testid="draft-new">
          <Plus size={12} /> New Draft
        </button>
      </div>
      {loading && rows.length === 0 ? <p className="font-mono text-xs text-[#757065]">Loading…</p> : (
        <div className="border border-[#2D2A26] bg-[#0a0a0a] divide-y divide-[#2D2A26]">
          {rows.length === 0 ? (
            <div className="px-4 py-10 text-center font-mono text-xs text-[#757065]">No drafts saved.</div>
          ) : rows.map((d) => (
            <div key={d.id} className="px-4 py-3 flex items-center gap-3" data-testid={`draft-row-${d.id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-display text-base text-[#E4DFD3] truncate">{d.name}</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#757065] truncate">{d.kind} · {d.subject}</div>
              </div>
              <button onClick={() => setEditing({ ...d })} className="border border-[#454038] text-[#9FA8D4] px-2 py-1 font-mono text-[10px] uppercase">Open</button>
              <button onClick={() => remove(d)} className="border border-[#D92D20]/40 text-[#D92D20] px-2 py-1 font-mono text-[10px] uppercase">Delete</button>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <div className="w-full max-w-2xl border border-[#D92D20]/40 bg-[#0F0F0F] p-5 space-y-3">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20]">// {editing.id ? "EDIT" : "NEW"} DRAFT</div>
            <input value={editing.name} onChange={(e) => setEditing((s) => ({ ...s, name: e.target.value }))} placeholder="Name" data-testid="draft-name"
              className="w-full bg-[#0a0a0a] border border-[#2D2A26] focus:border-[#D92D20] outline-none px-3 py-2 font-mono text-sm text-[#E4DFD3]" />
            <input value={editing.subject} onChange={(e) => setEditing((s) => ({ ...s, subject: e.target.value }))} placeholder="Subject" data-testid="draft-subject"
              className="w-full bg-[#0a0a0a] border border-[#2D2A26] focus:border-[#D92D20] outline-none px-3 py-2 font-mono text-sm text-[#E4DFD3]" />
            <textarea value={editing.body} onChange={(e) => setEditing((s) => ({ ...s, body: e.target.value }))} rows={10} placeholder="Body" data-testid="draft-body"
              className="w-full bg-[#0a0a0a] border border-[#2D2A26] focus:border-[#D92D20] outline-none px-3 py-2 font-mono text-sm text-[#E4DFD3] resize-y" />
            <div className="flex gap-2">
              <button onClick={save} data-testid="draft-save" className="flex-1 bg-[#D92D20] hover:bg-[#B92318] text-white px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em]">Save</button>
              <button onClick={() => setEditing(null)} className="px-3 py-2 border border-[#454038] text-[#A39E93] font-mono text-[10px] uppercase tracking-[0.25em]">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------- History ----------------------------------------------------

const HistoryTab = ({ token, userToken }) => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [filters, setFilters] = useState({ kind: "", status: "", search: "" });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        skip, limit: 50,
        ...(filters.kind ? { kind: filters.kind } : {}),
        ...(filters.status ? { status: filters.status } : {}),
        ...(filters.search ? { search: filters.search } : {}),
      };
      const r = await adminEmailHubRecentSends(token, params, userToken);
      setRows(r.rows || []); setTotal(r.total || 0);
    } catch { toast.error("Failed to load history"); }
    finally { setLoading(false); }
  }, [token, userToken, skip, filters]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="email-hub-history">
      <div className="grid sm:grid-cols-4 gap-2 mb-4">
        <input value={filters.search} onChange={(e) => { setSkip(0); setFilters((f) => ({ ...f, search: e.target.value })); }} placeholder="Search subject / recipient" data-testid="history-search"
          className="bg-[#0F0F0F] border border-[#2D2A26] focus:border-[#D92D20] outline-none px-3 py-2 font-mono text-sm text-[#E4DFD3] placeholder:text-[#757065]" />
        <select value={filters.kind} onChange={(e) => { setSkip(0); setFilters((f) => ({ ...f, kind: e.target.value })); }} data-testid="history-kind"
          className="bg-[#0F0F0F] border border-[#2D2A26] outline-none px-3 py-2 font-mono text-sm text-[#E4DFD3]">
          <option value="">All Kinds</option>
          <option value="individual">Individual</option>
          <option value="bulk">Bulk</option>
          <option value="newsletter">Newsletter</option>
          <option value="welcome">Welcome</option>
          <option value="dossier_release">Dossier Release</option>
        </select>
        <select value={filters.status} onChange={(e) => { setSkip(0); setFilters((f) => ({ ...f, status: e.target.value })); }} data-testid="history-status"
          className="bg-[#0F0F0F] border border-[#2D2A26] outline-none px-3 py-2 font-mono text-sm text-[#E4DFD3]">
          <option value="">All Statuses</option>
          <option value="delivered">Delivered</option>
          <option value="failed">Failed</option>
          <option value="bounced">Bounced</option>
          <option value="queued">Queued</option>
          <option value="queued_disabled">Queued (Disabled)</option>
        </select>
        <button onClick={load} className="flex items-center justify-center gap-2 border border-[#454038] hover:border-[#D92D20] text-[#A39E93] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em]" data-testid="history-refresh">
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      <div className="border border-[#2D2A26] bg-[#0a0a0a] divide-y divide-[#2D2A26]">
        {rows.length === 0 && !loading ? (
          <div className="px-4 py-10 text-center font-mono text-xs text-[#757065]">No sends match.</div>
        ) : rows.map((r, i) => {
          const color = STATUS_COLOR[r.status] || "#A39E93";
          return (
            <div key={r.id || i} className="px-4 py-3 grid grid-cols-12 gap-2 items-center text-sm" data-testid={`history-row-${r.id}`}>
              <div className="col-span-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#757065]">
                {r.created_at ? new Date(r.created_at).toLocaleString() : "—"}
              </div>
              <div className="col-span-2">
                <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#A39E93] border border-[#2D2A26] px-2 py-0.5">{r.kind || "—"}</span>
              </div>
              <div className="col-span-4 truncate text-[#E4DFD3]">{r.subject}</div>
              <div className="col-span-2 font-mono text-[10px] text-[#A39E93] truncate">{r.recipient_email}</div>
              <div className="col-span-2 text-right">
                <span className="font-mono text-[9px] uppercase tracking-[0.2em] border px-2 py-0.5" style={{ color, borderColor: color }}>{r.status}</span>
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex items-center justify-between mt-3">
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39E93]">{rows.length} of {total}</span>
        <div className="flex gap-2">
          <button onClick={() => setSkip(Math.max(0, skip - 50))} disabled={skip === 0} className="border border-[#454038] disabled:opacity-30 text-[#A39E93] px-2 py-1 font-mono text-[10px] uppercase">Prev</button>
          <button onClick={() => setSkip(skip + 50)} disabled={skip + rows.length >= total} className="border border-[#454038] disabled:opacity-30 text-[#A39E93] px-2 py-1 font-mono text-[10px] uppercase">Next</button>
        </div>
      </div>
    </div>
  );
};

export default EmailHub;
